/**
 * The matter's economics, drawn.
 *
 * Four pieces, each answering one question the KPI strip raises but cannot
 * answer on its own:
 *
 *   BudgetDonut       "how much of the fee is gone?" - billed and WIP against
 *                     the agreed fee, with the consumed share in the middle.
 *                     Only drawn when there IS a fee; a ring with no
 *                     denominator is decoration.
 *   RecordedTimeChart "when was the work done?" - hours per month from the
 *                     matter's own time entries. The CURRENT month is drawn
 *                     in the WIP weight, because it is the month still being
 *                     worked and has not settled into history yet.
 *   FeeEarnerBars     "who did it?" - avatar, hours, revenue, and a bar
 *                     relative to the biggest contributor.
 *   ActivityDonut     "on what?" - hours by activity type.
 *
 * Charts are Chart.js via react-chartjs-2 (already the app's charting library
 * - see Analytics and the firm economics widgets). Colours come from the brand
 * tokens through `useChartTokens`, so they flip with the theme; there is no hex
 * in this file.
 *
 * Every piece renders a graceful empty state rather than an empty axis when
 * its slice of the payload is absent. Nothing here is synthesised: if the
 * backend sends no `by_activity`, the activity panel says so.
 */

import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { PiChartBarLight, PiUsersLight } from 'react-icons/pi';
import GlassCard from '../../shared/brand/GlassCard';
import UserCell from './UserCell';
import { useChartTokens } from './chartTokens';
import { formatCurrency } from '../../../utils/formatCurrency';
import type { UserCellUser } from './UserCell';

// Idempotent registration of the elements used here.
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-full min-h-[8rem] items-center justify-center">
    <p className="text-sm text-fg-muted">{children}</p>
  </div>
);

/* ── Budget consumption ─────────────────────────────────────────── */

export interface BudgetDonutProps {
  budget: number;
  billed: number;
  wip: number;
  currency: string;
  /** Unclamped consumption percentage. Derived here if the caller omits it. */
  usedPct?: number;
  /** The true remainder, negative on overrun. Derived here if omitted. */
  remaining?: number | null;
}

/** Billed + WIP against the budget, with the consumed share in the middle. */
export const BudgetDonut: React.FC<BudgetDonutProps> = ({
  budget,
  billed,
  wip,
  currency,
  usedPct,
  remaining: actualRemaining,
}) => {
  const { t } = useTranslation();
  const tokens = useChartTokens();

  const consumed = billed + wip;
  /*
    Two different remainders, deliberately.

    `ringRemaining` is the empty part of the ring, so it FLOORS at zero - a
    negative slice would render as nothing or wrap. `actualRemaining` is the
    real figure from the rollup, negative on overrun, and it is what the
    tooltip reports. Same for the percentage: the caller's `usedPct` is
    unclamped (140% reads as 140%), it is only the geometry that is bounded.
  */
  const ringRemaining = Math.max(0, budget - consumed);
  const pct = usedPct ?? (budget > 0 ? Math.round((consumed / budget) * 100) : 0);
  const over = consumed > budget;
  const remainingLabel = actualRemaining ?? budget - consumed;

  const data = useMemo(() => ({
    labels: [t('Billed'), t('WIP'), t('Remaining')],
    datasets: [{
      data: [billed, wip, ringRemaining],
      backgroundColor: [
        over ? tokens.danger : tokens.champagne,
        tokens.champagneSoft,
        tokens.track,
      ],
      borderWidth: 0,
      // A thin ring: the number in the middle is the reading, the ring is the
      // proportion. A fat donut turns the hole into a label box.
      cutout: '76%',
    }],
  }), [billed, wip, ringRemaining, over, tokens, t]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            // The Remaining slice reports the TRUE figure, which the ring
            // itself cannot show once it goes negative.
            const value = ctx.dataIndex === 2 ? remainingLabel : Number(ctx.raw || 0);
            return `${ctx.label}: ${formatCurrency(Number(value), currency)}`;
          },
        },
      },
    },
  }), [currency, remainingLabel]);

  return (
    <div className="relative h-56">
      <Doughnut data={data} options={options} />
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span
          className={
            'text-2xl font-medium tabular-nums ' +
            (over ? 'text-danger-600 dark:text-danger-400' : 'text-fg-strong')
          }
        >
          {pct}%
        </span>
        <span className="text-[11px] text-fg-muted">{t('of budget used')}</span>
      </div>
    </div>
  );
};

/* ── Recorded time over the months ──────────────────────────────── */

export interface TimePoint {
  /** `YYYY-MM` - the key the current-month check compares against. */
  key: string;
  /** Display label, e.g. "2026-03". */
  label: string;
  hours: number;
  billableHours: number;
}

export interface RecordedTimeChartProps {
  series: TimePoint[];
  /** `YYYY-MM` of today - that bar is unsettled work, drawn at WIP weight. */
  currentKey: string;
}

/**
 * Hours per month, billable stacked under total.
 *
 * FLAGGED - this is RECORDED TIME, not invoiced money over time. A per-entry
 * amount would need each contributor's effective rate at the entry's date, and
 * the time-entry payload does not carry one. Charting hours is the honest
 * reading of the data actually in hand; inventing a revenue curve from an
 * average rate would be a fabricated number on a money screen.
 */
export const RecordedTimeChart: React.FC<RecordedTimeChartProps> = ({ series, currentKey }) => {
  const { t } = useTranslation();
  const tokens = useChartTokens();

  const data = useMemo(() => ({
    labels: series.map((p) => p.label),
    datasets: [
      {
        label: t('Billable'),
        data: series.map((p) => p.billableHours),
        backgroundColor: series.map((p) =>
          p.key === currentKey ? tokens.champagneSoft : tokens.champagne
        ),
        borderRadius: 3,
        stack: 'hours',
      },
      {
        label: t('Non-billable'),
        data: series.map((p) => Math.max(0, p.hours - p.billableHours)),
        backgroundColor: tokens.palette[0],
        borderRadius: 3,
        stack: 'hours',
      },
    ],
  }), [series, currentKey, tokens, t]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { color: tokens.muted, boxWidth: 8, boxHeight: 8, usePointStyle: true, font: { size: 10 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${Number(ctx.raw || 0).toFixed(2)} ${t('h')}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        border: { color: tokens.grid },
        ticks: { color: tokens.muted, font: { size: 10 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: tokens.grid },
        border: { display: false },
        ticks: { color: tokens.muted, font: { size: 10 }, precision: 0 },
      },
    },
  }), [tokens, t]);

  return (
    <div className="h-56">
      {series.length ? <Bar data={data} options={options} /> : <Empty>{t('No time recorded yet.')}</Empty>}
    </div>
  );
};

/* ── By fee earner ──────────────────────────────────────────────── */

export interface FeeEarnerRow {
  user: UserCellUser;
  label: string;
  hours: number;
  revenue: number;
}

export interface FeeEarnerBarsProps {
  rows: FeeEarnerRow[];
  currency: string;
  /** Fill the height of the grid row it sits in, so its neighbour matches it. */
  fill?: boolean;
}

/**
 * Who did the work - avatar, hours, revenue, and a bar relative to the
 * biggest contributor. A list rather than a chart, because the name is the
 * thing you are looking for and an axis of names reads worse than a roster.
 */
export const FeeEarnerBars: React.FC<FeeEarnerBarsProps> = ({ rows, currency, fill = false }) => {
  const { t } = useTranslation();
  const max = rows.reduce((m, r) => Math.max(m, r.hours), 0);

  return (
    <GlassCard
      fill={fill}
      // A filled card's body is the column; the roster owns the only scroller
      // so a long list cannot stretch the row it shares.
      bodyClassName={fill ? 'overflow-y-auto custom-scrollbar-main' : undefined}
      icon={<PiUsersLight />}
      title={t('By fee earner')}
      padding={rows.length ? 'none' : 'md'}
    >
      {rows.length === 0 ? (
        <Empty>{t('No data yet.')}</Empty>
      ) : (
        <ul className="divide-y divide-hairline-soft">
          {rows.map((row, index) => (
            <li key={`${row.label}-${index}`} className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <UserCell user={row.user} fallbackLabel={row.label} />
                <span className="flex-shrink-0 text-end">
                  <span className="block text-sm font-medium text-fg-body tabular-nums">
                    {row.hours.toFixed(2)} {t('h')}
                  </span>
                  <span className="block text-[11px] text-fg-muted tabular-nums">
                    {formatCurrency(row.revenue, currency)}
                  </span>
                </span>
              </div>
              <div className="mt-2 h-1 w-full rounded-full bg-surface-sunken overflow-hidden">
                <div
                  className="h-full rounded-full bg-champagne transition-all duration-300 ease-slide"
                  style={{ width: `${max > 0 ? (row.hours / max) * 100 : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </GlassCard>
  );
};

/* ── By activity ────────────────────────────────────────────────── */

export interface ActivityRow {
  label: string;
  hours: number;
}

export const ActivityDonut: React.FC<{ rows: ActivityRow[]; fill?: boolean }> = ({ rows, fill = false }) => {
  const { t } = useTranslation();
  const tokens = useChartTokens();

  const data = useMemo(() => ({
    labels: rows.map((r) => r.label),
    datasets: [{
      data: rows.map((r) => r.hours),
      backgroundColor: rows.map((_, i) => tokens.palette[i % (tokens.palette.length || 1)]),
      borderWidth: 0,
      cutout: '62%',
    }],
  }), [rows, tokens]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: { color: tokens.muted, boxWidth: 8, boxHeight: 8, usePointStyle: true, font: { size: 10 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.label}: ${Number(ctx.raw || 0).toFixed(2)} ${t('h')}`,
        },
      },
    },
  }), [tokens, t]);

  return (
    <GlassCard
      fill={fill}
      bodyClassName={fill ? 'justify-center' : undefined}
      icon={<PiChartBarLight />}
      title={t('By activity')}
    >
      <div className="h-[13.5rem] w-full">
        {rows.length ? (
          <Doughnut data={data} options={options} />
        ) : (
          <Empty>{t('No activity breakdown available for this matter.')}</Empty>
        )}
      </div>
    </GlassCard>
  );
};
